import { useState, useEffect, useRef } from "react";
import { DateTime } from "luxon";
import { getLongDateFormat } from "../../utils/luxonHelpers";
import i18n from "../../utils/i18n";
import Meals from "./Meals";
import DateAndTimeInput from "./DateAndTimeInput";
import { calculateMeals } from "../../utils/calculateMeals";
import { getDateTime } from "../../utils/getDateTime";
import {
  getDateTimeFromPicker,
  getTimeFromInput,
} from "../../utils/dateTimeConverters";

const ShiftsBatchFormDateInput = ({
  index,
  has_remove_button,
  removePicker,
  picker,
  onChange,
  from_label,
  to_label,
}) => {
  const dateTimePickerRef = useRef(null);
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);

  const longDateFormat = getLongDateFormat();

  const [startDate, setStartDate] = useState(
    picker.start_date
      ? DateTime.fromFormat(picker.start_date, longDateFormat)
      : null
  );
  const [startTime, setStartTime] = useState(
    picker.start_time ? getTimeFromInput(picker.start_time) : null
  );
  const [endDate, setEndDate] = useState(
    picker.end_date
      ? DateTime.fromFormat(picker.end_date, longDateFormat)
      : null
  );
  const [endTime, setEndTime] = useState(
    picker.end_time ? getTimeFromInput(picker.end_time) : null
  );
  const [lunch, setLunch] = useState(null);
  const [dinner, setDinner] = useState(null);

  // Initialize inputmask on mount. Inputmask's own API rather than its jQuery plugin, so this
  // component does not need jQuery from the Sprockets bundle to mask its time fields.
  useEffect(() => {
    if (!dateTimePickerRef.current) return;

    // Inputmask's own API rather than its jQuery plugin, and one instance per field (an instance
    // holds per-element state). `window.Inputmask` is the copy the Sprockets bundle already loads:
    // importing the npm package here instead puts a second copy on the page, and the two of them
    // leave the field masked but inert.
    dateTimePickerRef.current.querySelectorAll(".time_input").forEach((input) => {
      window.Inputmask({
        alias: "datetime",
        inputFormat: "HH:mm",
        placeholder: i18n.t("schedule.shifts.form.inputmask_placeholder"),
        showMaskOnHover: false,
      }).mask(input);
    });
  }, []);

  // Calculate meals when dates/times change
  useEffect(() => {
    const state = { startDate, startTime, endDate, endTime };
    const startDateTime = getDateTime("start", state);
    const endDateTime = getDateTime("end", state);

    if (startDateTime && endDateTime) {
      const meals = calculateMeals(startDateTime, endDateTime);
      setLunch(meals.lunch);
      setDinner(meals.dinner);
      onChange(index, "lunch", meals.lunch);
      onChange(index, "dinner", meals.dinner);
    }
  }, [startDate, startTime, endDate, endTime, index]); // Removed onChange from dependencies

  const handleRemoveClick = () => {
    removePicker(index);
  };

  const startDateHandleChange = (e) => {
    const dateObj = getDateTimeFromPicker(e.date);
    if (!dateObj) return;

    setStartDate(dateObj);
    setEndDate(dateObj);

    const formattedDate = dateObj.toFormat(longDateFormat);

    onChange(index, e.target.name, formattedDate);
    onChange(index, "end_date", formattedDate);
    startTimeInputRef.current?.focus();
  };

  const startTimeHandleChange = (e) => {
    const timeValue = e.target.value.trim();
    const parsedTime = getTimeFromInput(timeValue);
    setStartTime(parsedTime);
    onChange(index, e.target.name, timeValue);
  };

  const endDateHandleChange = (e) => {
    const dateObj = getDateTimeFromPicker(e.date);
    if (!dateObj) return;

    setStartDate(startDate || dateObj);
    setEndDate(dateObj);

    const formattedDate = dateObj.toFormat(longDateFormat);

    onChange(index, e.target.name, formattedDate);
    endTimeInputRef.current?.focus();
  };

  const endTimeHandleChange = (e) => {
    const timeValue = e.target.value.trim();
    const parsedTime = getTimeFromInput(timeValue);
    setEndTime(parsedTime);
    onChange(index, e.target.name, timeValue);
  };

  return (
    <fieldset ref={dateTimePickerRef} className="datetimepicker-wrapper">
      {has_remove_button && (
        <button
          type="button"
          className="btn-remove-picker"
          onClick={handleRemoveClick}
          aria-label={i18n.t("schedule.shifts.form.remove_shift_row")}
        >
          <i className="fa fa-times" aria-hidden="true"></i>
        </button>
      )}
      <div className="form-group">
        <label>{from_label}</label>
        <DateAndTimeInput
          type="start"
          selectedDate={startDate}
          selectedTime={startTime}
          onDateChange={startDateHandleChange}
          onTimeBlur={startTimeHandleChange}
          timeInputRef={startTimeInputRef}
        />
      </div>
      <div className="form-group">
        <label>{to_label}</label>
        <DateAndTimeInput
          type="end"
          selectedDate={endDate}
          selectedTime={endTime}
          onDateChange={endDateHandleChange}
          onTimeBlur={endTimeHandleChange}
          timeInputRef={endTimeInputRef}
        />
      </div>

      <Meals lunch={lunch} dinner={dinner} />
    </fieldset>
  );
};

export default ShiftsBatchFormDateInput;
